/**
 * Áp thương hiệu của tổ chức lên trang (sau đăng nhập, hoặc trước đăng nhập khi ở tên miền
 * riêng). Chỉ áp những gì tổ chức TỰ ĐẶT — tổ chức chưa đặt gì (vd Trà Dược trên tên miền
 * chung) giữ nguyên giao diện hiện tại.
 */
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { urlThuongHieu } from '@/lib/config'
import type { Branding } from '@/lib/branding'

export interface ThuongHieuRieng {
  brandName?: string | null
  tagline?: string | null
  primaryColor?: string | null
  /** Màu nền thanh menu trên cùng; null = mặc định. */
  headerColor?: string | null
  pageTitle?: string | null
  faviconUrl?: string | null
}

/** #rrggbb → "h s% l%" (định dạng biến --primary của giao diện). */
export function hexSangHsl(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h *= 60
  }
  return `${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`
}

const cacMauHex = (v: string): string[] => (v.match(/#[0-9a-f]{6}/gi) ?? []).map((x) => x.toLowerCase())
const doSangHex = (hex: string): number => {
  const n = parseInt(hex.replace('#', ''), 16)
  const kenh = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4 })
  return 0.2126 * kenh[0] + 0.7152 * kenh[1] + 0.0722 * kenh[2]
}

/**
 * Bộ màu thanh header từ màu nền (màu đơn hoặc gradient): chữ sáng/tối theo độ sáng TRUNG BÌNH
 * các điểm màu, hover & viền dẫn xuất từ điểm đầu; `anh` = gradient để vẽ nền (null nếu màu đơn).
 */
export function boMauHeader(mau: string): { nen: string; chu: string; hover: string; vien: string; toi: boolean; anh: string | null } | null {
  const ds = cacMauHex(mau)
  if (!ds.length) return null
  const hsl = hexSangHsl(ds[0])
  if (!hsl) return null
  const [h, s, l] = hsl.split(' ').map((x) => parseFloat(x))
  const toi = ds.reduce((t, x) => t + doSangHex(x), 0) / ds.length < 0.4
  const lech = (d: number) => `${h} ${s}% ${Math.max(0, Math.min(100, l + (toi ? d : -d))).toFixed(1)}%`
  const laGd = mau.trim().toLowerCase().startsWith('linear-gradient(') && ds.length > 1
  return { nen: `${h} ${s}% ${l}%`, chu: toi ? '210 40% 96%' : '222 47% 11%', hover: lech(8), vien: lech(6), toi, anh: laGd ? mau : null }
}

const BIEN_HEADER = ['--sidebar', '--sidebar-foreground', '--sidebar-accent', '--sidebar-border', '--header-bg'] as const

export function apDungMauHeader(mau: string | null | undefined): void {
  const st = document.documentElement.style
  const bo = mau ? boMauHeader(mau) : null
  if (!bo) { for (const v of BIEN_HEADER) st.removeProperty(v); return }
  st.setProperty('--sidebar', bo.nen)
  st.setProperty('--sidebar-foreground', bo.chu)
  st.setProperty('--sidebar-accent', bo.hover)
  st.setProperty('--sidebar-border', bo.vien)
  if (bo.anh) st.setProperty('--header-bg', bo.anh); else st.removeProperty('--header-bg')
}

export function apDungThuongHieu(t: ThuongHieuRieng): void {
  const nap: Branding = { ...(window.__CHATMQL_BRANDING__ ?? {}) }
  // Tổ chức đặt tên riêng → không mượn khẩu hiệu của nền tảng.
  if (t.brandName) { nap.brandName = t.brandName; nap.tagline = t.tagline ?? '' }
  else if (t.tagline) nap.tagline = t.tagline
  if (t.primaryColor) {
    // Gradient: nút / link dùng màu đầu (chữ trên nền gradient khó đọc), nền lớn dùng --brand-gradient.
    nap.primaryColor = t.primaryColor
    const don = cacMauHex(t.primaryColor)[0]
    const hsl = don ? hexSangHsl(don) : null
    if (hsl) {
      document.documentElement.style.setProperty('--primary', hsl)
      document.documentElement.style.setProperty('--brand-color', don)
      document.documentElement.style.setProperty('--brand-gradient', t.primaryColor.startsWith('linear-gradient(') ? t.primaryColor : `linear-gradient(135deg, ${don}, ${don}b3)`)
    }
  }
  if (t.headerColor !== undefined) apDungMauHeader(t.headerColor)
  if (t.faviconUrl) {
    nap.faviconUrl = t.faviconUrl
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (icon) icon.href = urlThuongHieu(t.faviconUrl)
  }
  const tieuDe = t.pageTitle || (t.brandName ? `${t.brandName}${nap.tagline ? ` — ${nap.tagline}` : ''}` : null)
  if (tieuDe) document.title = tieuDe
  window.__CHATMQL_BRANDING__ = nap
}

interface CaiDatThuongHieu {
  brandName: string | null; tagline: string | null; primaryColor: string | null; headerColor: string | null; pageTitle: string | null
  coFavicon: boolean; hieuLuc: { faviconUrl: string }
}

/** Sau đăng nhập: áp thương hiệu tổ chức (chỉ trường đã đặt). */
export function useThuongHieuToChuc(): void {
  const { data } = useQuery<CaiDatThuongHieu>({
    queryKey: ['settings', 'branding'],
    queryFn: async () => (await api.get<CaiDatThuongHieu>('/settings/branding')).data,
    staleTime: 5 * 60_000,
  })
  useEffect(() => {
    if (!data) return
    apDungThuongHieu({
      brandName: data.brandName, tagline: data.tagline, primaryColor: data.primaryColor, headerColor: data.headerColor, pageTitle: data.pageTitle,
      faviconUrl: data.coFavicon ? data.hieuLuc.faviconUrl : null,
    })
  }, [data])
}
